import { useCallback, useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc
} from "firebase/firestore";
import * as XLSX from "xlsx";
import Sidebar from "../components/Sidebar";
import { auth, db } from "../firebase";

const ENTRY_TYPES = [
  { value: "INVOICE", label: "Invoice revenue" },
  { value: "PAYROLL", label: "Payroll cost" },
  { value: "EXPENSE", label: "Operating expense" },
  { value: "RECEIPT", label: "Customer receipt" },
  { value: "CASH", label: "Cash / bank balance" },
  { value: "PAYABLE", label: "Payable" },
  { value: "STATUTORY", label: "Statutory liability" }
];

const MANAGEMENT_CLIENTS = [
  "PTC AKF",
  "ARSL",
  "ATL",
  "ATL CocaCola",
  "AESL",
  "E-Commerce",
  "Corteva Agrisciences",
  "Sefam Pvt Ltd",
  "G-Solar",
  "IIL",
  "CBRE",
  "Nagina Electronics",
  "United Detergent",
  "ALM"
];

const emptyForm = {
  period: new Date().toISOString().slice(0, 7),
  type: "INVOICE",
  date: new Date().toISOString().slice(0, 10),
  client: "",
  reference: "",
  category: "",
  amount: "",
  dueDate: "",
  notes: ""
};

const money = (value) => `PKR ${Math.round(Number(value || 0)).toLocaleString("en-PK")}`;
const timestampMs = (value) => value?.toMillis?.() || 0;

export default function ManagementFinance() {
  const [entries, setEntries] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [periodFilter, setPeriodFilter] = useState(emptyForm.period);
  const [clientFilter, setClientFilter] = useState("ALL");
  const [tab, setTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [entrySnapshot, periodSnapshot] = await Promise.all([
        getDocs(collection(db, "management_finance_entries")),
        getDocs(collection(db, "management_finance_periods"))
      ]);
      setEntries(entrySnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      setPeriods(periodSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    } catch (loadError) {
      setError(loadError?.message || "Management finance data could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const activePeriod = periods.find((item) => item.id === form.period);
  const periodLocked = activePeriod?.status === "LOCKED";
  const clients = useMemo(
    () => [...new Set([...MANAGEMENT_CLIENTS, ...entries.map((item) => item.client).filter(Boolean)])],
    [entries]
  );

  const filteredEntries = useMemo(() => entries
    .filter((item) => !periodFilter || item.period === periodFilter)
    .filter((item) => clientFilter === "ALL" || item.client === clientFilter)
    .sort((a, b) => (b.date || "").localeCompare(a.date || "") || timestampMs(b.createdAt) - timestampMs(a.createdAt)),
  [entries, periodFilter, clientFilter]);

  const approvedEntries = filteredEntries.filter((item) => item.status === "APPROVED");
  const total = (type) => approvedEntries
    .filter((item) => item.type === type)
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const revenue = total("INVOICE");
  const payroll = total("PAYROLL");
  const expenses = total("EXPENSE");
  const receipts = total("RECEIPT");
  const payables = total("PAYABLE") + total("STATUTORY");
  const margin = revenue - payroll - expenses;
  const receivables = Math.max(0, revenue - receipts);
  const cashEntries = approvedEntries.filter((item) => item.type === "CASH");
  const latestCash = cashEntries.sort((a, b) => (b.date || "").localeCompare(a.date || ""))[0];

  const saveEntry = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");
    if (periodLocked) {
      setError("This period is locked. Reopen it before adding another entry.");
      return;
    }
    if (!form.period || !form.date || !form.reference.trim() || !(Number(form.amount) > 0)) {
      setError("Period, date, reference and an amount greater than zero are required.");
      return;
    }
    if (["INVOICE", "RECEIPT"].includes(form.type) && !form.client.trim()) {
      setError("Client is required for invoice revenue and customer receipts.");
      return;
    }
    const duplicate = entries.some((item) =>
      item.type === form.type &&
      String(item.reference || "").trim().toLowerCase() === form.reference.trim().toLowerCase()
    );
    if (duplicate) {
      setError("This reference already exists for the selected entry type.");
      return;
    }
    setSaving(true);
    try {
      await addDoc(collection(db, "management_finance_entries"), {
        ...form,
        client: form.client.trim(),
        reference: form.reference.trim(),
        category: form.category.trim(),
        notes: form.notes.trim(),
        amount: Number(form.amount),
        status: "DRAFT",
        source: "MANUAL",
        createdByUid: auth.currentUser?.uid || null,
        createdByEmail: auth.currentUser?.email || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      setForm((current) => ({ ...emptyForm, period: current.period, date: current.date }));
      setMessage("Draft entry saved. Review and approve it before it affects dashboard totals.");
      await loadData();
    } catch (saveError) {
      setError(saveError?.message || "The entry could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const approveEntry = async (entry) => {
    if (periods.find((item) => item.id === entry.period)?.status === "LOCKED") {
      setError("This period is locked and cannot be changed.");
      return;
    }
    await updateDoc(doc(db, "management_finance_entries", entry.id), {
      status: "APPROVED",
      approvedByUid: auth.currentUser?.uid || null,
      approvedByEmail: auth.currentUser?.email || null,
      approvedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    setMessage(`${entry.reference} approved and included in dashboard totals.`);
    await loadData();
  };

  const removeDraft = async (entry) => {
    if (entry.status !== "DRAFT") return;
    await deleteDoc(doc(db, "management_finance_entries", entry.id));
    setMessage(`${entry.reference} removed.`);
    await loadData();
  };

  const setPeriodStatus = async (period, status) => {
    if (!period) {
      setError("Select a month first.");
      return;
    }
    const unapproved = entries.filter((item) => item.period === period && item.status !== "APPROVED").length;
    if (status === "LOCKED" && unapproved) {
      setError(`Approve or remove ${unapproved} draft record(s) before locking ${period}.`);
      return;
    }
    await setDoc(doc(db, "management_finance_periods", period), {
      period,
      status,
      updatedByUid: auth.currentUser?.uid || null,
      updatedByEmail: auth.currentUser?.email || null,
      updatedAt: serverTimestamp()
    }, { merge: true });
    setMessage(`${period} is now ${status.toLowerCase()}.`);
    await loadData();
  };

  const exportRecords = () => {
    const rows = filteredEntries.map((item) => ({
      Period: item.period,
      Date: item.date,
      Type: ENTRY_TYPES.find((type) => type.value === item.type)?.label || item.type,
      Client: item.client,
      Reference: item.reference,
      Category: item.category,
      Amount: Number(item.amount || 0),
      Status: item.status,
      Notes: item.notes,
      "Entered By": item.createdByEmail
    }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), "Management Finance");
    XLSX.writeFile(workbook, `Management-Finance-${periodFilter || "All"}.xlsx`);
  };

  return (
    <div className="app-shell">
      <Sidebar />
      <main className="main-content management-finance">
        <header className="page-header">
          <div>
            <div className="eyebrow">Management reporting</div>
            <h1 className="page-title">Manual Finance Dashboard</h1>
            <p className="page-copy">A separate, approval-based ledger. Existing invoice, payroll and attendance records are not changed.</p>
          </div>
          <span className="status-pill warning">Manual data source</span>
        </header>

        <div className="management-tabs" role="tablist">
          {[{ id: "dashboard", label: "Dashboard" }, { id: "entry", label: "Add Data" }, { id: "records", label: "Records" }, { id: "periods", label: "Period Control" }].map((item) => (
            <button key={item.id} className={`management-tab ${tab === item.id ? "active" : ""}`} onClick={() => setTab(item.id)} type="button">{item.label}</button>
          ))}
        </div>

        {(message || error) && <div className={`management-alert ${error ? "error" : "success"}`}>{error || message}</div>}

        {tab === "dashboard" && <>
          <section className="panel management-filter-panel">
            <div className="panel-body management-filters">
              <div className="field"><label>Reporting month</label><input className="input" type="month" value={periodFilter} onChange={(e) => setPeriodFilter(e.target.value)} /></div>
              <div className="field"><label>Client</label><select className="input" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}><option value="ALL">All clients</option>{clients.map((client) => <option key={client} value={client}>{client}</option>)}</select></div>
              <div className="management-source-note">Only approved manual entries are included.</div>
            </div>
          </section>
          <section className="management-metrics">
            <article className="metric-card"><div className="metric-label">Invoice revenue</div><div className="metric-value">{money(revenue)}</div></article>
            <article className="metric-card"><div className="metric-label">Payroll cost</div><div className="metric-value">{money(payroll)}</div></article>
            <article className="metric-card"><div className="metric-label">Operating expenses</div><div className="metric-value">{money(expenses)}</div></article>
            <article className="metric-card"><div className="metric-label">Operating contribution</div><div className={`metric-value ${margin < 0 ? "negative" : ""}`}>{money(margin)}</div></article>
            <article className="metric-card"><div className="metric-label">Estimated receivables</div><div className="metric-value">{money(receivables)}</div><div className="metric-footnote">Invoices less receipts in selected scope</div></article>
            <article className="metric-card"><div className="metric-label">Payables & statutory</div><div className="metric-value">{money(payables)}</div></article>
            <article className="metric-card"><div className="metric-label">Latest cash balance</div><div className="metric-value">{latestCash ? money(latestCash.amount) : "No data"}</div><div className="metric-footnote">{latestCash?.date || "Add a cash/bank balance"}</div></article>
          </section>
          <section className="panel">
            <div className="panel-header"><div><h2 className="panel-title">Approved records</h2><p className="page-copy">Source records behind the displayed totals.</p></div></div>
            <RecordsTable entries={approvedEntries} loading={loading} />
          </section>
        </>}

        {tab === "entry" && <section className="panel">
          <div className="panel-header"><div><h2 className="panel-title">Add manual finance data</h2><p className="page-copy">New records begin as drafts and do not affect totals until approved.</p></div><span className={`status-pill ${periodLocked ? "danger" : "success"}`}>{periodLocked ? "Period locked" : "Period open"}</span></div>
          <form className="panel-body" onSubmit={saveEntry}>
            <div className="form-grid">
              <div className="field"><label>Month *</label><input className="input" type="month" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} /></div>
              <div className="field"><label>Entry type *</label><select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>{ENTRY_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}</select></div>
              <div className="field"><label>Transaction date *</label><input className="input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <div className="field"><label>Client</label><select className="input" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })}><option value="">Select client</option>{MANAGEMENT_CLIENTS.map((client) => <option key={client} value={client}>{client}</option>)}</select></div>
              <div className="field"><label>Reference *</label><input className="input" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Invoice, payroll or voucher number" /></div>
              <div className="field"><label>Category</label><input className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Salary, GST, rent, bank balance..." /></div>
              <div className="field"><label>Amount (PKR) *</label><input className="input" type="number" min="0.01" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
              <div className="field"><label>Due date</label><input className="input" type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} /></div>
            </div>
            <div className="field management-notes"><label>Notes</label><textarea className="textarea" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div className="button-row"><button className="btn btn-primary" disabled={saving || periodLocked} type="submit">{saving ? "Saving..." : "Save Draft"}</button></div>
          </form>
        </section>}

        {tab === "records" && <section className="panel">
          <div className="panel-header"><div><h2 className="panel-title">Review manual records</h2><p className="page-copy">Approve correct drafts or remove them before the month is locked.</p></div><button className="btn btn-muted" type="button" onClick={exportRecords} disabled={!filteredEntries.length}>Export Excel</button></div>
          <div className="panel-body management-filters compact"><div className="field"><label>Month</label><input className="input" type="month" value={periodFilter} onChange={(e) => setPeriodFilter(e.target.value)} /></div><div className="field"><label>Client</label><select className="input" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}><option value="ALL">All clients</option>{clients.map((client) => <option key={client} value={client}>{client}</option>)}</select></div></div>
          <RecordsTable entries={filteredEntries} loading={loading} onApprove={approveEntry} onDelete={removeDraft} />
        </section>}

        {tab === "periods" && <section className="panel">
          <div className="panel-header"><div><h2 className="panel-title">Period control</h2><p className="page-copy">Lock a completed month to prevent additional entries or approvals.</p></div></div>
          <div className="panel-body">
            <div className="management-period-control"><div className="field"><label>Month</label><input className="input" type="month" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} /></div><span className={`status-pill ${periodLocked ? "danger" : "success"}`}>{periodLocked ? "Locked" : "Open"}</span><button className={`btn ${periodLocked ? "btn-muted" : "btn-danger"}`} type="button" onClick={() => setPeriodStatus(form.period, periodLocked ? "OPEN" : "LOCKED")}>{periodLocked ? "Reopen Period" : "Lock Period"}</button></div>
            <p className="page-copy">Locking is allowed only when every record in the month has been approved. Reopening is recorded with the acting user and timestamp.</p>
          </div>
        </section>}
      </main>
    </div>
  );
}

function RecordsTable({ entries, loading, onApprove, onDelete }) {
  if (loading) return <div className="panel-body page-copy">Loading management records...</div>;
  if (!entries.length) return <div className="empty-state"><strong>No records found</strong><p className="page-copy">Add data or change the selected reporting month.</p></div>;
  return <div className="table-wrap"><table className="data-table"><thead><tr><th>Date</th><th>Type</th><th>Client</th><th>Reference</th><th>Category</th><th>Amount</th><th>Status</th>{(onApprove || onDelete) && <th>Actions</th>}</tr></thead><tbody>{entries.map((entry) => <tr key={entry.id}><td>{entry.date}</td><td>{ENTRY_TYPES.find((type) => type.value === entry.type)?.label || entry.type}</td><td>{entry.client || "—"}</td><td>{entry.reference}</td><td>{entry.category || "—"}</td><td>{money(entry.amount)}</td><td><span className={`status-pill ${entry.status === "APPROVED" ? "success" : "warning"}`}>{entry.status}</span></td>{(onApprove || onDelete) && <td><div className="table-actions">{entry.status === "DRAFT" && onApprove && <button className="btn btn-success" type="button" onClick={() => onApprove(entry)}>Approve</button>}{entry.status === "DRAFT" && onDelete && <button className="btn btn-danger" type="button" onClick={() => onDelete(entry)}>Remove</button>}</div></td>}</tr>)}</tbody></table></div>;
}
